import React from 'react';

import {
  IconApps,
  IconAppsFilled,
  IconBooks,
  IconBrandYoutube,
  IconBrandYoutubeFilled,
  IconChartAreaLine,
  IconChartAreaLineFilled,
  IconChartHistogram,
  IconChecklist,
  IconChevronsRight,
  IconClipboardCheck,
  IconClipboardCheckFilled,
  IconClipboardList,
  IconClipboardListFilled,
  IconFileDescription,
  IconFileDescriptionFilled,
  IconLayoutDashboard,
  IconLayoutDashboardFilled,
  IconSchool,
  IconSchoolFilled,
  IconUser,
  IconUserFilled,
  IconVideo,
  IconVideoFilled,
} from '@tabler/icons-react';
import _ from 'lodash';

import {
  ADMIN_CLASSES_HREF,
  ADMIN_FOLLOW_UP_HREF,
  ADMIN_LEGACY_EXAM_ASSIGNMENT_HREF,
  ADMIN_PEOPLE_HREF,
  ADMIN_SUBJECTS_HREF,
  ANALYTICS_HREF,
  APPS_HREF,
  DASHBOARD_HREF,
  NOTES_HREF,
  ROLL_HREF,
  TEACHER_CLASSES_HREF,
  TEACHER_LEGACY_EXAMS_HREF,
  TEACHER_LEGACY_MARKING_HREF,
  TUTORIALS_HREF,
  VIDEOS_HREF,
} from '../../utils/common/hrefs';
import { getAppEndpoints, getAppOptions } from './apps';
import { hasOverlap } from './utils';

import { UserGroup } from '@repo/validation';

// A single navigation entry shared by the desktop drawer and the mobile bottom
// navigation. `iconFilled` (the Tabler filled variant) is swapped in when the
// item is selected; items without one keep the outline icon and rely on the
// selected colour. `importance` (lower = higher priority, 0 being the highest)
// promotes an item into the pinned mobile tabs; items without it never pin.
export interface MenuOption {
  icon: React.ReactElement | React.ReactElement[];
  iconFilled?: React.ReactElement | React.ReactElement[];
  name: string;
  href: string;
  // Endpoints that should mark the option as selected.
  linkedEndpoints: string[];
  importance?: number;
}

// A group of menu options rendered under an optional heading.
export interface MenuSection {
  header?: string;
  items: MenuOption[];
}

/**
 * Top-level items, rendered without a heading
 */

const dashboardItem: MenuOption = {
  icon: <IconLayoutDashboard />,
  iconFilled: <IconLayoutDashboardFilled />,
  name: 'Dashboard',
  href: DASHBOARD_HREF,
  linkedEndpoints: [''],
  importance: 0,
};

const rollItem: MenuOption = {
  icon: <IconChecklist />,
  name: 'Roll',
  href: ROLL_HREF,
  linkedEndpoints: ['roll'],
  importance: 1,
};

const followUpItem: MenuOption = {
  icon: <IconChevronsRight />,
  name: 'Follow up',
  href: ADMIN_FOLLOW_UP_HREF,
  linkedEndpoints: ['admin/follow-up'],
};

/**
 * Admin
 */

const peopleItem: MenuOption = {
  icon: <IconUser />,
  iconFilled: <IconUserFilled />,
  name: 'People',
  href: ADMIN_PEOPLE_HREF,
  linkedEndpoints: ['admin/people', 'admin/student', 'admin/staff', 'admin/lead'],
  importance: 2,
};

const adminClassesItem: MenuOption = {
  icon: <IconSchool />,
  iconFilled: <IconSchoolFilled />,
  name: 'Classes',
  href: ADMIN_CLASSES_HREF,
  linkedEndpoints: ['admin/classes', 'admin/class'],
  importance: 2,
};

const teacherClassesItem: MenuOption = {
  icon: <IconSchool />,
  iconFilled: <IconSchoolFilled />,
  name: 'Classes',
  href: TEACHER_CLASSES_HREF,
  linkedEndpoints: ['teacher/classes', 'teacher/class'],
  importance: 2,
};

const subjectsItem: MenuOption = {
  icon: <IconBooks />,
  name: 'Subjects',
  href: ADMIN_SUBJECTS_HREF,
  // The whole content subtree a subject leads to, so the entry stays selected
  // wherever the reader is inside it
  linkedEndpoints: [
    'admin/subjects',
    'admin/course',
    'admin/legacy-exams',
    'admin/legacy-topics',
    'admin/notes',
    'admin/resources',
    'admin/video',
    'admin/videos',
  ],
};

/**
 * Service
 */

const tutorialsItem: MenuOption = {
  icon: <IconVideo />,
  iconFilled: <IconVideoFilled />,
  name: 'Tutorials',
  href: TUTORIALS_HREF,
  linkedEndpoints: ['tutorials'],
  importance: 3,
};

const examAssignmentItem: MenuOption = {
  icon: <IconClipboardList />,
  iconFilled: <IconClipboardListFilled />,
  name: 'Exam assignment',
  href: ADMIN_LEGACY_EXAM_ASSIGNMENT_HREF,
  linkedEndpoints: ['admin/legacy-exam-assignment'],
};

const markingItem: MenuOption = {
  icon: <IconClipboardCheck />,
  iconFilled: <IconClipboardCheckFilled />,
  name: 'Marking',
  href: TEACHER_LEGACY_MARKING_HREF,
  linkedEndpoints: ['teacher/legacy-marking'],
};

/**
 * Content
 */

const notesItem: MenuOption = {
  icon: <IconFileDescription />,
  iconFilled: <IconFileDescriptionFilled />,
  name: 'Notes',
  href: NOTES_HREF,
  linkedEndpoints: ['notes'],
  importance: 3,
};

const videosItem: MenuOption = {
  icon: <IconBrandYoutube />,
  iconFilled: <IconBrandYoutubeFilled />,
  name: 'Videos',
  href: VIDEOS_HREF,
  linkedEndpoints: ['videos', 'video'],
  importance: 3,
};

const examsItem: MenuOption = {
  icon: <IconChartHistogram />,
  name: 'Exams',
  href: TEACHER_LEGACY_EXAMS_HREF,
  linkedEndpoints: ['teacher/legacy-exams'],
};

const analyticsItem: MenuOption = {
  icon: <IconChartAreaLine />,
  iconFilled: <IconChartAreaLineFilled />,
  name: 'Analytics',
  href: ANALYTICS_HREF,
  linkedEndpoints: ['analytics'],
};

/**
 * Footer
 */

const appsItem: MenuOption = {
  icon: <IconApps />,
  iconFilled: <IconAppsFilled />,
  name: 'Apps',
  href: APPS_HREF,
  linkedEndpoints: getAppEndpoints(),
};

/** Apps short enough a list to read among the sections rather than a gallery. */
const MAX_INLINE_APPS = 2;

// The role-specific sections, before any apps join them.
const getRoleSections = (userGroups: string[]): MenuSection[] => {
  const isAdmin = hasOverlap(userGroups, [UserGroup.Admins]);
  const isManager = hasOverlap(userGroups, [UserGroup.Managers]);
  const isTeacher = hasOverlap(userGroups, [UserGroup.Teachers]);
  const isStudent = hasOverlap(userGroups, [UserGroup.Students]);
  const isStaff = isManager || isAdmin || isTeacher;
  // Everyone who reads the shared library of tutorials, notes and videos
  const isContentReader = isStudent || isAdmin || isTeacher;

  const topItems = [
    dashboardItem,
    ...(isAdmin || isTeacher ? [rollItem] : []),
    ...(isAdmin ? [followUpItem] : []),
  ];

  // Classes is the one item whose destination differs by role, so an admin who
  // also teaches keeps the admin view of it.
  const adminItems = _.uniqBy(
    [
      ...(isAdmin ? [peopleItem, adminClassesItem] : []),
      ...(isTeacher ? [teacherClassesItem] : []),
      ...(isAdmin ? [subjectsItem] : []),
    ],
    'name'
  );

  const serviceItems = [
    ...(isContentReader ? [tutorialsItem] : []),
    ...(isAdmin ? [examAssignmentItem] : []),
    ...(isTeacher ? [markingItem] : []),
  ];

  const contentItems = [
    ...(isContentReader ? [notesItem, videosItem] : []),
    ...(isAdmin || isTeacher ? [examsItem] : []),
    ...(isStudent ? [analyticsItem] : []),
  ];

  // An owner is neither staff nor a student, and holds no page of these
  // sections — their apps are appended to whatever the roles here compose
  return !isStaff && !isStudent
    ? []
    : [
        { items: topItems },
        ...(adminItems.length > 0 ? [{ header: 'Admin', items: adminItems }] : []),
        ...(serviceItems.length > 0 ? [{ header: 'Service', items: serviceItems }] : []),
        ...(contentItems.length > 0 ? [{ header: 'Content', items: contentItems }] : []),
      ];
};

// Composes the ordered, role-specific navigation sections. This is the single
// source of truth consumed by both the desktop drawer and the mobile bottom
// navigation, so the two stay in sync.
export const getMenuSections = (userGroups: string[]): MenuSection[] => {
  const sections = getRoleSections(userGroups);
  const apps = getAppOptions(userGroups);

  // A short list reads as a section of its own; a longer one is left to the
  // gallery `getFooterMenuOptions` pins.
  return apps.length > 0 && apps.length <= MAX_INLINE_APPS
    ? [...sections, { header: 'Apps', items: apps }]
    : sections;
};

// The gallery entry pinned below the scrolling sections, which a list too long
// to sit among them falls back to.
export const getFooterMenuOptions = (userGroups: string[]): MenuOption[] =>
  getAppOptions(userGroups).length > MAX_INLINE_APPS ? [appsItem] : [];
